import React, { useEffect, useRef, useState } from 'react'
import { Flex, Text } from '@chakra-ui/react'
import { keyframes } from '@emotion/react'

import LoadingDots from '~components/shared/loading-dots'
import SharedStack from '~components/shared/wrappers/shared-stack'
import { MascotAvatar, useClinicMascot } from './mascot'

const bob = keyframes`
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-3px); }
`

interface AskChckvetThinkingProps {
    /** Server start time in ms, when the first poll has arrived. */
    startedAtMs?: number | null
    toolCallCount?: number
    /** True once streamed assistant text is on screen. */
    isStreaming?: boolean
}

/**
 * In-thread status while Ask Chckvet is working. Clinic-agent turns often
 * sit on tool calls for a while before any tokens arrive, so a static
 * spinner reads as stuck. This keeps the wait alive with phase copy,
 * elapsed time, and a compact footer once the reply starts streaming.
 */
export default function AskChckvetThinking({
    startedAtMs,
    toolCallCount = 0,
    isStreaming = false,
}: AskChckvetThinkingProps) {
    const { mascot } = useClinicMascot()
    const localStartedAtRef = useRef(Date.now())
    const now = useNow(true)
    const startedAt = startedAtMs ?? localStartedAtRef.current
    const elapsedSeconds = Math.max(0, Math.floor((now - startedAt) / 1000))
    const copy = getThinkingCopy({
        elapsedSeconds,
        toolCallCount,
        isStreaming,
    })

    if (isStreaming) {
        return (
            <Flex align="center" gap={2} mt={2} color="gray.500" minH="20px">
                <LoadingDots color="brand.400" size="5px" speed={1.1} />
                <Text fontSize="xs">
                    {copy.title}
                    {elapsedSeconds >= 8 ? ` · ${elapsedSeconds}s` : ''}
                </Text>
            </Flex>
        )
    }

    return (
        <Flex
            align="flex-start"
            gap={3}
            bg="gray.50"
            borderWidth={1}
            borderColor="gray.200"
            rounded="xl"
            px={3}
            py={3}
            w="full"
        >
            <Flex
                animation={`${bob} 1.4s ease-in-out infinite`}
                sx={{
                    '@media (prefers-reduced-motion: reduce)': {
                        animation: 'none',
                    },
                }}
                flexShrink={0}
            >
                <MascotAvatar mascot={mascot} size={32} />
            </Flex>
            <SharedStack spacing={1} minW={0} flex={1} pt="2px">
                <Flex align="center" gap={2}>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                        {copy.title}
                    </Text>
                    <LoadingDots color="brand.400" size="6px" speed={1.1} />
                </Flex>
                {copy.detail && (
                    <Text fontSize="xs" color="gray.500">
                        {copy.detail}
                    </Text>
                )}
                {elapsedSeconds >= 8 && (
                    <Text fontSize="xs" color="gray.400">
                        {elapsedSeconds}s elapsed
                    </Text>
                )}
            </SharedStack>
        </Flex>
    )
}

function useNow(active: boolean): number {
    const [now, setNow] = useState(() => Date.now())

    useEffect(() => {
        if (!active) return undefined
        const id = window.setInterval(() => setNow(Date.now()), 1000)
        return () => window.clearInterval(id)
    }, [active])

    return now
}

function getThinkingCopy({
    elapsedSeconds,
    toolCallCount,
    isStreaming,
}: {
    elapsedSeconds: number
    toolCallCount: number
    isStreaming: boolean
}): { title: string; detail?: string } {
    if (isStreaming) {
        return {
            title:
                toolCallCount > 0
                    ? 'Still checking clinic data'
                    : 'Writing a reply',
        }
    }

    if (toolCallCount >= 4) {
        return {
            title: 'Putting it together',
            detail: 'Reviewing what I found in your clinic',
        }
    }

    if (toolCallCount >= 1) {
        return {
            title: 'Looking through your clinic',
            detail: 'Checking settings and records',
        }
    }

    if (elapsedSeconds >= 30) {
        return {
            title: 'Still working',
            detail: 'This one is taking a bit longer than usual',
        }
    }

    if (elapsedSeconds >= 8) {
        return {
            title: 'Looking through your clinic',
            detail: 'Checking settings and records',
        }
    }

    if (elapsedSeconds >= 3) {
        return { title: 'Looking through your clinic' }
    }

    return { title: 'Thinking' }
}
